import React, {useEffect, useState} from 'react';
import {useSearchParams, Link} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';
import styled from "styled-components";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { TableText, TableTextWithLink } from '../../elements/Table/common';
import Table from "../../elements/Table"
import MainCard from "../../elements/MainCard";
import Spin from '../../elements/Loader/spin';
import {formatDate, getPhoneNumber} from '../../utils/globalFunctions';
import {GEEKIFY_ME_TABLE_ROWS_PER_PAGE} from "../../utils/constant";

import * as actions from "../../store/actions";
import AppliedJobFilter from '../filter/appliedJobFilter';

const EyeIcon = styled(VisibilityOutlinedIcon)(() => ({
  fill: '#283665'
}));

const AppliedJobList = () => {
  const localRowsPerPage = localStorage.getItem(GEEKIFY_ME_TABLE_ROWS_PER_PAGE)
  const [page, setPage] = useState(0);
  const [filterString, setFilterString] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(localRowsPerPage?.trim()?.length > 0 ? +localRowsPerPage : 10);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const params = Object.fromEntries([...searchParams]);

  const { appliedJobs, isLoading } = useSelector((state) => {
    return {
      appliedJobs: state?.appliedJobs?.appliedJobs || [],
      isLoading: state?.appliedJobs?.isLoading || false,
    }
  });

  useEffect(() => {
    const url = new URLSearchParams(window.location.search || "");
    const initial = {};
    url.forEach((value, key) => {
      initial[key] = value;
    });

    if (initial.page) {
      setPage(Math.max(0, parseInt(initial.page, 10) - 1));
    }

    if (initial.limit) {
      setRowsPerPage(+initial.limit);
    } else if (localRowsPerPage) {
      setRowsPerPage(+localRowsPerPage);
      url.set("limit", +localRowsPerPage);
      setSearchParams({ ...Object.fromEntries(url.entries()) });
      setFilterString(url.toString());
    }
  }, [])

  useEffect(() => {
    const spObj = Object.fromEntries([...searchParams]);
    if (!spObj.page || !spObj.limit) return;
    const qs = `?${new URLSearchParams(spObj).toString()}`;
    dispatch(actions.getAppliedJobs(qs));
  }, [searchParams]);

  useEffect(() => {
    const current = { ...(Object.fromEntries([...searchParams])) };
    current.page = params?.page || page + 1;
    current.limit = rowsPerPage;
    setSearchParams(current);
    setFilterString(new URLSearchParams(current).toString());
  }, [page, rowsPerPage]);

  const columns = [
    {
      dataKey: 'job',
      label: 'Job',
      style: {width: "10%"},
      renderer: (_, row) => {
        const primaryText = `${row?.jobId?.title}`;
        return TableText(primaryText);
      }
    },
    {
      dataKey: 'company',
      label: 'Company',
      style: {width: "10%"},
      renderer: (_, row) => {
        const primaryText = `${row?.jobId?.businessId?.name}`;
        return TableText(primaryText);
      }
    },
    {
      dataKey: 'email',
      label: 'Email',
      style: { width: "10%" },
    },
    {
      dataKey: 'mobile',
      label: 'Phone',
      style: { width: "10%" },
      renderer: (_, row) => {
        return TableText(getPhoneNumber(row?.mobile?.phoneCode, row?.mobile?.phone))
      }
    },
    {
      dataKey: 'dates',
      label: 'Applied Date',
      style: { width: "10%" },
      renderer: (_, row) => {
        const primaryText = formatDate(row?.createdAt);
        return TableText(
          primaryText,
          null,
          false,
          "Created At",
        );
      }
    },
    {
      dataKey: 'resume',
      label: 'Resume',
      style: { width: "5%" },
      renderer: (_, row) => {
        return (
          <div>
            {row?.resume ?
              (
                <Link target='_blank' to={`${row?.resume}`}>
                  <EyeIcon />
                </Link>
              )
              : null
            }
          </div>
        )
      }
    },
  ];

  const debounceRef = React.useRef(null);
  const applyFilter = (filter) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPage(0);

      const sp = Object.fromEntries(new URLSearchParams(filter).entries());
      const localRows = localRowsPerPage?.trim()?.length > 0 ? +localRowsPerPage : 10;
      setRowsPerPage(localRows);

      sp.page = "1";
      sp.limit = `${localRows}`;

      setSearchParams(sp);
      setFilterString(new URLSearchParams(sp).toString());
    }, 300);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, [])

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    const params = new URLSearchParams(window.location.search || "");
    params.set("page", newPage + 1);
    setFilterString(params.toString());
    setSearchParams(Object.fromEntries(params.entries()));
  };

  const handleChangeRowsPerPage = (event) => {
    localStorage.setItem(GEEKIFY_ME_TABLE_ROWS_PER_PAGE, +event.target.value)
    setRowsPerPage(+event.target.value);
    setPage(0);
    const params = new URLSearchParams(window.location.search || "");
    params.set("page", "1");
    params.set("limit", event.target.value);
    setFilterString(params.toString());
    setSearchParams(Object.fromEntries(params.entries()));
  };

  return (
    <MainCard style={{ borderRadius: '12px', maxWidth: '1358px', margin: '0px auto' }}>
      <div className="icon-left layout-header">
        <h1>Applied Jobs</h1>
        <AppliedJobFilter applyFilter={applyFilter}/>
      </div>
      <div className="layout-container">
        <Spin isLoading={isLoading}>
          <div className='applied-jobs'>
            <Table
              columns={columns}
              rows={appliedJobs.results}
              count={appliedJobs.totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </Spin>
      </div>
    </MainCard>
  )
}

export default AppliedJobList
